import { HeatSeries, HeatMix } from "src/store/api/generatedApi";
import { Typography } from "@mui/material";
import { useMemo } from "react";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useTenantTranslation } from "hooks/formatters";
import { ValueWithDiff } from "../ValueWithDiff";
import { equalSets } from "../utils";
import { useHeatsSelection } from "components/strategyDashboard/heats/context/HeatsSelectionContext";

const isBackupSource = (source: string, suggestedMissing: boolean): boolean => {
  return source === "suggestedSource" && suggestedMissing;
};

export const HeatCostCell = ({
  row,
  source,
  ignoredMaterials,
}: {
  row: HeatSeries;
  source: string;
  ignoredMaterials: Set<string>;
}) => {
  const { t } = useTenantTranslation();
  const { format } = useNumberSerialiser();
  const { selectedSummaryName } = useHeatsSelection();

  const mix: HeatMix | undefined = row.mixes.find(
    (mix) =>
      mix.source === source &&
      equalSets(new Set(mix.ignored_materials), ignoredMaterials)
  );
  const suggestedMissing = !row.mixes.some(
    (mix) =>
      mix.source === "suggestedSource" &&
      equalSets(new Set(mix.ignored_materials), ignoredMaterials)
  );

  const summary = mix?.summary_data.find(
    (summary) => summary.name === selectedSummaryName
  );

  const content = useMemo(() => {
    if (mix?.is_equivalent_to_previous) {
      return <Typography sx={{ color: "text.disabled" }}>{"="}</Typography>;
    }

    if (isBackupSource(source, suggestedMissing)) {
      return <Typography>{t("backup")}</Typography>;
    }

    if (!summary) {
      return <Typography>-</Typography>;
    }

    return (
      <ValueWithDiff
        variant="inherit"
        summary={summary}
        formatNumber={format}
        hideDiff={
          source === "suggestedSource" ||
          (suggestedMissing && source === "deployedSource")
        }
      />
    );
  }, [mix, source, suggestedMissing, summary, format, t]);

  return content;
};
